import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authService } from '../services/api';

const Settings = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  // Temp user conversion state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState('');
  const [conversionSuccess, setConversionSuccess] = useState('');

  // Regular user settings state
  const [newUsername, setNewUsername] = useState('');
  const [usernameCurrentPassword, setUsernameCurrentPassword] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Helper function to extract error message from API response
  const extractErrorMessage = (error, fallbackMessage) => {
    // Handle legacy format
    if (error.response?.data?.error && error.response.data.error.code) {
      return t(error.response.data.error.code) || error.response.data.error.message;
    }
    // Handle simple detail string
    if (error.response?.data?.detail && typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return fallbackMessage;
  };

  const handleTempUserConversion = async (e) => {
    e.preventDefault();

    if (!username.trim() || !email.trim() || !password || !confirmPassword) {
      setConversionError(t('error.allFieldsRequired') || 'All fields are required');
      return;
    }

    if (password !== confirmPassword) {
      setConversionError(t('error.passwordMismatch') || 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setConversionError(t('error.passwordTooShort') || 'Password must be at least 6 characters long');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setConversionError(t('error.invalidEmail') || 'Please enter a valid email address');
      return;
    }

    setConversionLoading(true);
    setConversionError('');
    setConversionSuccess('');

    try {
      const result = await authService.convertTempUser(username.trim(), email.trim(), password);
      setConversionSuccess(result.message || 'Account successfully converted');

      // Refresh the page to update user data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      const errorMessage = extractErrorMessage(error, t('error.conversionFailed') || 'Account conversion failed');
      setConversionError(errorMessage);
    } finally {
      setConversionLoading(false);
    }
  };

  const handleUsernameUpdate = async (e) => {
    e.preventDefault();

    if (!newUsername.trim() || !usernameCurrentPassword) {
      setUsernameError(t('error.allFieldsRequired') || 'All fields are required');
      return;
    }

    if (newUsername.trim() === currentUser?.username) {
      setUsernameError(t('error.usernameUnchanged') || 'New username must be different from current username');
      return;
    }

    setUsernameLoading(true);
    setUsernameError('');
    setUsernameSuccess('');

    try {
      const result = await authService.updateUsername(newUsername.trim(), usernameCurrentPassword);
      setUsernameSuccess(result.message || 'Username updated successfully');
      setNewUsername('');
      setUsernameCurrentPassword('');

      // Refresh user data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      const errorMessage = extractErrorMessage(error, t('error.updateFailed') || 'Update failed');
      setUsernameError(errorMessage);
    } finally {
      setUsernameLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      setPasswordError(t('error.allFieldsRequired') || 'All fields are required');
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setPasswordError(t('error.passwordMismatch') || 'New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t('error.passwordTooShort') || 'Password must be at least 6 characters long');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError(t('error.passwordUnchanged') || 'New password must be different from current password');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      const result = await authService.updatePassword(currentPassword, newPassword);
      setPasswordSuccess(result.message || 'Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (error) {
      const errorMessage = extractErrorMessage(error, t('error.updateFailed') || 'Update failed');
      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#075e54] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-2 text-gray-500">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // Error display component for better consistency
  const ErrorDisplay = ({ error }) => {
    if (!error) return null;

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  };

  // Success display component for better consistency
  const SuccessDisplay = ({ message }) => {
    if (!message) return null;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-green-800">{message}</p>
          </div>
        </div>
      </div>
    );
  };

  // Render different content based on user type
  if (currentUser.is_temp) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">
              {t('settings.completeProfile') || 'Complete Your Profile'}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('settings.tempUserMessage') || 'Please set up your account with a username, email, and password to continue.'}
            </p>
          </div>

          <div className="p-6">
            {/* Current Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">
                {t('settings.currentInfo') || 'Current Information'}
              </h2>
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-blue-700">
                    {t('settings.phoneNumber') || 'Phone Number'}:
                  </span>
                  <span className="text-sm text-blue-900 ml-2">{currentUser.phone_number || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-blue-700">
                    {t('settings.clinicId') || 'Clinic ID'}:
                  </span>
                  <span className="text-sm text-blue-900 ml-2">{currentUser.clid}</span>
                </div>
              </div>
            </div>

            {/* Conversion Form */}
            <form onSubmit={handleTempUserConversion} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  {t('settings.username') || 'Username'} *
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterUsername') || 'Enter your username'}
                  disabled={conversionLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('settings.email') || 'Email'} *
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterEmail') || 'Enter your email address'}
                  disabled={conversionLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('settings.password') || 'Password'} *
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterPassword') || 'Enter your password'}
                  disabled={conversionLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  {t('settings.confirmPassword') || 'Confirm Password'} *
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.confirmPassword') || 'Confirm your password'}
                  disabled={conversionLoading}
                  required
                />
              </div>

              <ErrorDisplay error={conversionError} />
              <SuccessDisplay message={conversionSuccess} />

              <div>
                <button
                  type="submit"
                  disabled={conversionLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {conversionLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('common.processing') || 'Processing...'}
                    </div>
                  ) : (
                    t('settings.completeSetup') || 'Complete Account Setup'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Regular user settings
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('settings.title') || 'Account Settings'}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('settings.subtitle') || 'Manage your account preferences and security settings'}
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Current User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('settings.currentInfo') || 'Current Account Information'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('settings.username') || 'Username'}
                </label>
                <p className="mt-1 text-sm text-gray-900">{currentUser.username}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('settings.email') || 'Email'}
                </label>
                <p className="mt-1 text-sm text-gray-900">{currentUser.email || 'Not set'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('settings.clinicId') || 'Clinic ID'}
                </label>
                <p className="mt-1 text-sm text-gray-900">{currentUser.clid}</p>
              </div>
            </div>
          </div>

          {/* Username Update Form */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('settings.updateUsername') || 'Update Username'}
            </h2>

            <form onSubmit={handleUsernameUpdate} className="space-y-4">
              <div>
                <label htmlFor="newUsername" className="block text-sm font-medium text-gray-700">
                  {t('settings.newUsername') || 'New Username'}
                </label>
                <input
                  id="newUsername"
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterNewUsername') || 'Enter new username'}
                  disabled={usernameLoading}
                />
              </div>

              <div>
                <label htmlFor="usernameCurrentPassword" className="block text-sm font-medium text-gray-700">
                  {t('settings.currentPassword') || 'Current Password'}
                </label>
                <input
                  id="usernameCurrentPassword"
                  type="password"
                  value={usernameCurrentPassword}
                  onChange={(e) => setUsernameCurrentPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterCurrentPassword') || 'Enter current password'}
                  disabled={usernameLoading}
                />
              </div>

              <ErrorDisplay error={usernameError} />
              <SuccessDisplay message={usernameSuccess} />

              <div>
                <button
                  type="submit"
                  disabled={usernameLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {usernameLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('common.updating') || 'Updating...'}
                    </div>
                  ) : (
                    t('settings.updateUsername') || 'Update Username'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Password Update Form */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('settings.updatePassword') || 'Update Password'}
            </h2>

            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                  {t('settings.currentPassword') || 'Current Password'}
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterCurrentPassword') || 'Enter current password'}
                  disabled={passwordLoading}
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  {t('settings.newPassword') || 'New Password'}
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.enterNewPassword') || 'Enter new password'}
                  disabled={passwordLoading}
                />
              </div>

              <div>
                <label htmlFor="newPasswordConfirm" className="block text-sm font-medium text-gray-700">
                  {t('settings.confirmPassword') || 'Confirm New Password'}
                </label>
                <input
                  id="newPasswordConfirm"
                  type="password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] sm:text-sm"
                  placeholder={t('settings.confirmNewPassword') || 'Confirm new password'}
                  disabled={passwordLoading}
                />
              </div>

              <ErrorDisplay error={passwordError} />
              <SuccessDisplay message={passwordSuccess} />

              <div>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('common.updating') || 'Updating...'}
                    </div>
                  ) : (
                    t('settings.updatePassword') || 'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
